import type React from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import type { ServicePartnerPageDto } from "@/api/types";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { Container } from "@/design-system/Container";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useSlug } from "@/hooks/useSlug";
import { servicePartnerMutations } from "@/queries/service-partners/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ServiceCard } from "./ServiceCard";

interface ServicesGridViewProps {
  services: ServicePartnerPageDto[];
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}

export function ServicesGridView({ services, hasMore, isLoading, onLoadMore }: ServicesGridViewProps): React.ReactNode {
  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const slug = useSlug();
  const { t } = useTranslation();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("delete-service-modal");

  const mutationDeleteService = servicePartnerMutations.useDeleteServicePartner();

  const loaderRef = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoading && hasMore) {
        onLoadMore();
      }
    }, [hasMore, isLoading, onLoadMore]),
  });

  return (
    <>
      <Stack gap="md">
        <Container fullWidth>
          <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
            {services.length === 0 && !isLoading ? (
              <span className="text-body-emphasis">
                {sessionUser.isAdmin
                  ? t("page.bookings.list-services.no-services.admin")
                  : t("page.bookings.list-services.no-services.resident")}
              </span>
            ) : (
              services.map((service) => (
                <ServiceCard
                  data-testid="service-card"
                  onDelete={() => openDeleteModal(service.id)}
                  onEdit={() => void navigate(routes.servicePartners.edit({ slug, id: service.id }))}
                  key={service.id}
                  {...{ service }}
                />
              ))
            )}
          </Tiles>
        </Container>
        {hasMore && <CollectionLoadingState ref={loaderRef} />}
      </Stack>
      <DeleteModal
        title={t("model.service.action.delete.confirmation")}
        description={t("model.service.action.delete.description")}
        onDelete={mutationDeleteService.mutateAsync}
        {...deleteModalProps}
      />
    </>
  );
}
